import React, { ChangeEvent, FC, useContext, useEffect, useMemo, useState } from 'react';
import Section from '../../../components/Main/Section/Section';
import { useTranslation } from 'react-i18next';
import { CurrentViewContext } from '../../../providers/CurrentViewProvider';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  createFilterOptions,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import ActionBar from '../../../components/ActionBar/ActionBar';
import { DoNotDisturbAlt, RateReview } from '@mui/icons-material';
import Filter from '../../../components/Filter/Filter';
import { IGmbLocation } from '../../../shared/interfaces/IGmbLocation';
import GMBReview from '../../../components/GoogleMyBusiness/Review/GMBReview';
import GMBService from '../../../services/GMBService';
import { GMBAuthContext } from '../../../providers/GMBAuthProvider';
import Pagination from '../../../components/Pagination/Pagination';
import { IGMBReviewComment, IGMBReviewEntry } from '../../../shared/interfaces/IGMBReviewEntry';
import GoogleMyBusinessLogin from '../../../components/GoogleMyBusiness/GoogleMyBusinessLogin';
import Chip from '../../../components/Chip/Chip';
import { IGMBPaging } from '../../../shared/interfaces/IGMBPaging';
import LeftContainer from '../../../components/Containers/LeftContainer';
import RightContainer from '../../../components/Containers/RightContainer';
import SortContainer from '../../../components/Containers/SortContainer';
import ContentContainer from '../../../components/Containers/ContentContainer';
import SliderStarsAsLabels from '../../../components/Sliders/StarsAsLabels/SliderStarsAsLabels';
import DateRange from '../../../components/DateRange/DateRange';
import { format, isValid } from 'date-fns';
import { AuthContext } from '../../../providers/AuthProvider';
import GoogleLocationInfo from '../../../components/Review/Elements/GoogleLocationInfo';
import CommentingService, {
  CommentStatus,
  FilterOptions,
} from '../../../services/CommentingService';
import CommentStatusFilter from '../../../components/CommentStatusFilter/CommentStatusFilter';
import TableBulkActionBar, {
  TableBulkAction,
} from '../../../components/ActionBar/TableBulkActionBar';
import { COMMENT_GENERATION_LANGUAGES } from '../../../constants/commentGenerationLanguages';
import CommentAiService, { IGenerateCommentOptions } from '../../../services/CommentAiService';
import { constants } from '../../../constants/constants';

const defaultLocation = { title: '', id: '' };

const Reviews: FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { setCurrentView } = useContext(CurrentViewContext);
  const { user, login } = useContext(GMBAuthContext);
  const { hasRole } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState<IGmbLocation[]>([]);
  const [reviewEntries, setReviewEntries] = useState<IGMBReviewEntry[]>([]);

  // Filters & Pagination
  const [commentStatus, setCommentStatus] = useState<CommentStatus>('');
  const [orderBy, setOrderBy] = useState<string>('date desc');
  const [location, setLocation] = useState<{ title: string; id: string }>(defaultLocation);
  const [score, setScore] = useState<number[]>([1, 5]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [filterByLocation, setFilterByLocation] = useState<IGmbLocation | null>(null);
  const [withComments, setWithComments] = useState<boolean>(true);
  const [withoutComments, setWithoutComments] = useState<boolean>(true);
  const [withDeletion, setWithDeletion] = useState<boolean>(false);
  const [withDeleted, setWithDeleted] = useState<boolean>(false);
  const [withNote, setWithNote] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(10);
  const [offset, setOffset] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [paging, setPaging] = useState<IGMBPaging>({
    pagesTotal: 0,
    totalItems: 0,
  });
  const [isUpdated, setIsUpdated] = useState<boolean>(false);
  const [currentUpdatedStatus, setCurrentUpdatedStatus] = useState<boolean>(false);

  // Triggers
  const [applyFilter, setApplyFilter] = useState<boolean>(false);

  // Errors
  const [noRecordsFound, setNoRecordsFound] = useState<boolean>(false);

  // bulk actions
  const [selectedReviewIds, setSelectedReviewIds] = useState<string[]>([]);
  const visibleReviewIds = useMemo(() => reviewEntries.map((review) => review.id), [reviewEntries]);
  const bulkActions = useMemo<TableBulkAction[]>(() => {
    const actions: TableBulkAction[] = [];
    if (hasRole('can-use-ai')) {
      actions.push({
        id: 'generate-comment',
        label: t('generate_comment'),
        initialInput: {
          language: 'de',
        },
        renderAdditionalElements: ({ value, onChange, disabled }) => (
          <FormControl
            size="small"
            sx={{ minWidth: 200 }}
            disabled={disabled}
          >
            <InputLabel id="generate-comment-language-select-label">{t('language')}</InputLabel>
            <Select
              labelId="generate-comment-language-select-label"
              value={value.language || 'de'}
              label={t('language')}
              onChange={(event: SelectChangeEvent) =>
                onChange({
                  ...value,
                  language: event.target.value,
                })
              }
            >
              {COMMENT_GENERATION_LANGUAGES.map(({ value: languageValue, labelKey }) => (
                <MenuItem
                  key={languageValue}
                  value={languageValue}
                >
                  {t(labelKey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ),
        onExecute: async (selectedIds: string[], input: Record<string, any>) => {
          if (!selectedIds.length) {
            return;
          }
          setLoading(true);
          for (const reviewEntry of reviewEntries) {
            if (selectedIds.includes(reviewEntry.id)) {
              const meta = reviewEntry.selectionMeta;

              if (reviewEntry.review.reviewReply?.comment) {
                // skip the ones that are already answered
                console.log('Skip');
                continue;
              }

              if (meta && meta.communityManagement && meta.reviewMetaData) {
                const communityManagement = meta.communityManagement;
                const generateCommentOptions: IGenerateCommentOptions = {
                  language: input.language,
                  tonality: constants.tonality[communityManagement.tonality.preference],
                  duzen: communityManagement.pronoun.preference === 0,
                  review: meta.AIInput,
                  useCustomerName: communityManagement.useUsername,
                  salutation: communityManagement.salutationPrefix,
                  signature: communityManagement.greeting,
                  additionalPrompt: communityManagement.additionalPrompt,
                  reviewMetadata: meta.reviewMetaData,
                  noReviewText: !!meta.noReviewText,
                };
                const text = await CommentAiService.generateComment(
                  generateCommentOptions,
                  () => {}
                );
                const result = await CommentingService.saveReviewComment(
                  reviewEntry.id,
                  reviewEntry?.reviewComment?.status || '',
                  text,
                  () => {}
                );
                if (result && result.reviewComment) {
                  const updatedReviewComment = {
                    ...result.reviewComment,
                    expanded: true,
                  };

                  setReviewEntries((prevState) =>
                    prevState.map((review) =>
                      review.id === reviewEntry.id
                        ? {
                            ...review,
                            reviewComment: updatedReviewComment,
                          }
                        : review
                    )
                  );
                }
                console.log('Saved comment', result);
              }
            }
          }
          setLoading(false);
        },
      });
      actions.push({
        id: 'post-comment',
        initialInput: {},
        label: t('post_comment'),
        onExecute: async (selectedIds: string[], input: Record<string, any>) => {
          if (!selectedIds.length) {
            return;
          }
          setLoading(true);
          for (const reviewEntry of reviewEntries) {
            if (selectedIds.includes(reviewEntry.id)) {
              const meta = reviewEntry.selectionMeta;

              if (reviewEntry.review.reviewReply?.comment) {
                // skip the ones that are already answered
                continue;
              }

              const comment = reviewEntry.reviewComment?.text;

              if (comment) {
                const reviewId = reviewEntry.review.reviewId;
                const revewLocation = getReviewLocation(reviewEntry.company);
                const result = await GMBService.replyToReview(
                  revewLocation.ID,
                  reviewId,
                  comment,
                  () => {}
                );
                if (!result) {
                  continue;
                }

                await CommentingService.saveReviewComment(
                  reviewEntry.id,
                  'DONE',
                  comment,
                  () => {}
                );
                setReviewEntries((prevState) =>
                  prevState.map((review) =>
                    review.id === reviewEntry.id
                      ? {
                          ...review,
                          reviewComment: {
                            ...(review.reviewComment as IGMBReviewComment),
                            text: comment,
                            status: 'DONE',
                          },
                        }
                      : review
                  )
                );

                console.log('Pres', result);

                /*if (result && result.reviewComment) {
                  result.reviewComment.expanded = true;
                  reviewEntry.reviewComment = result.reviewComment;
                }*/
                console.log('Posted comment');
              }
            }
          }
          setLoading(false);
        },
      });
    }
    return actions;
  }, [t, reviewEntries]);

  const handleReviewSelectionChange = (reviewId: string, selected: boolean) => {
    setSelectedReviewIds((prevState) => {
      if (selected) {
        if (prevState.includes(reviewId)) {
          return prevState;
        }
        return [...prevState, reviewId];
      }
      return prevState.filter((id) => id !== reviewId);
    });
  };

  const handleToggleSelectAllVisible = (checked: boolean) => {
    setSelectedReviewIds((prevState) => {
      if (checked) {
        return Array.from(new Set([...prevState, ...visibleReviewIds]));
      }

      return prevState.filter((id) => !visibleReviewIds.includes(id));
    });
  };

  const handleReviewCommentChange = (
    reviewEntryId: string,
    updates: Partial<IGMBReviewComment>
  ) => {
    setReviewEntries((prevState) =>
      prevState.map((reviewEntry) => {
        if (reviewEntry.id !== reviewEntryId) {
          return reviewEntry;
        }

        return {
          ...reviewEntry,
          reviewComment: {
            ...(reviewEntry.reviewComment as IGMBReviewComment),
            ...updates,
          },
        };
      })
    );
  };

  const prepareFilter = () => {
    setOffset(0);
    setPage(1);
    setApplyFilter(false);
  };

  const applyFilterWithReset = () => {
    setOffset(0);
    setPage(1);
    setApplyFilter(true);
  };

  const handleDeletionChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;

    setWithDeletion(isChecked);
    setApplyFilter(false);
  };

  const handleNoteChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;

    setWithNote(isChecked);

    setApplyFilter(false);
  };

  const handleOrderByChange = (event: SelectChangeEvent) => {
    setOrderBy(event.target.value);
    applyFilterWithReset();
  };

  const handleLocationFilterChange = (event: any, newValue: any) => {
    setFilterByLocation(newValue);
    setReviewEntries([]);
    setPaging((prevState) => ({
      ...prevState,
      pagesTotal: 0,
      totalItems: 0,
    }));
    setLocation({
      title: newValue?.locationData?.title || '',
      id: newValue?.locationID || '',
    });
    prepareFilter();
  };

  const handleScoreFilterChange = (event: Event, newValue: number | number[]) => {
    setScore(newValue as number[]);
    setApplyFilter(false);
  };

  const handleOnFromDateChange = (newValue: Date | null) => {
    setStartDate(newValue);
    setApplyFilter(false);
  };

  const handleOnToDateChange = (newValue: Date | null) => {
    setEndDate(newValue);
    setApplyFilter(false);
  };

  const handlePageNumberChange = (event: ChangeEvent<unknown>, pageNumber: number) => {
    setPage(pageNumber);
    setOffset(pageSize * pageNumber - pageSize);
    setApplyFilter(true);
  };

  const handlePageSizeChange = (event: SelectChangeEvent) => {
    setPageSize(parseInt(event.target.value));
    setPaging((prevState) => ({
      ...prevState,
      pagesTotal: 0,
      totalItems: 0,
    }));
    applyFilterWithReset();
  };

  useEffect(() => {
    setCurrentView(t('nav_reviews'));
    document.title = `${t('app_branding')} ${t('nav_reviews')}`;
  }, [setCurrentView, t]);

  useEffect(() => {
    if (!user) return;
    const controller = new AbortController();

    const getLocations = async (signal: AbortSignal) => {
      const data = await GMBService.getAllLocations(undefined, 1000, setLoading, signal, true);

      if (!data) return;
      setLocations(data.locations);
    };

    (async () => {
      await getLocations(controller.signal);
    })();

    return () => {
      controller.abort();
    };
  }, [user]);

  useEffect(() => {
    if (!applyFilter) return;

    const filter = async () => {
      if (!user) return;
      const controller = new AbortController();

      const cfilter: FilterOptions = {
        score,
        pageSize,
        offset,
        withComments,
        withoutComments,
        hasNotes: withNote,
        isUpdated,
        isDeleted: withDeleted,
        withGmb: true,
        withMonitoring: false,
        type: 'MONITORINGS',
      };

      if (startDate != null && isValid(startDate)) {
        cfilter.startDate = startDate.toISOString();
      }
      if (endDate != null && isValid(endDate)) {
        cfilter.endDate = endDate.toISOString();
      }
      if (orderBy && orderBy.length) {
        cfilter.orderBy = [orderBy];
      }
      if (location && location.id) {
        cfilter.company = location.id;
        cfilter.platform = 'google';
      }
      if (withDeletion) {
        cfilter.hasDeletion = true;
      }
      if (commentStatus) {
        cfilter.commentStatus = commentStatus;
      }

      setLoading(true);
      const data = await CommentingService.getAllReviews(controller, cfilter);
      setLoading(false);

      if (!data.reviews) return;
      if (data.reviews.length === 0) {
        setNoRecordsFound(true);
      } else {
        setNoRecordsFound(false);
      }

      setReviewEntries(data.reviews);
      setPaging(data.paging);
    };
    (async () => await filter())();
  }, [
    applyFilter,
    withComments,
    withoutComments,
    endDate,
    location,
    offset,
    orderBy,
    page,
    pageSize,
    score,
    startDate,
    user,
    withNote,
    withDeletion,
    withDeleted,
    commentStatus,
  ]);

  const renderedLocationFilterName = (props: any, option: any, state: any, ownerState: any) => {
    const { key, ...optionProps } = props;
    const location: IGmbLocation = option;
    return (
      <Box
        key={key}
        component="li"
        {...optionProps}
      >
        <Box>
          <Typography>{location.locationData.title}</Typography>
          <Typography sx={{ fontSize: '10px' }}>
            {`(${location.locationData.address.addressLines}, ${location.locationData.address.postalCode} ${location.locationData.address.locality})`}
          </Typography>
        </Box>
      </Box>
    );
  };

  const getReviewLocation = (company: string) => {
    const reviewLocation = locations.filter((f) => f.locationID === company)[0];
    return reviewLocation;
  };

  return (
    <Section>
      <>
        {!user ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              borderRadius: `${theme.shape.borderRadius}px`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <GoogleMyBusinessLogin login={login} />
          </Box>
        ) : (
          <ContentContainer>
            <LeftContainer>
              <ActionBar>
                {/*<Checkbox
                        disabled
                        checked={multiActionChecked}
                        onChange={handleActionCheckboxChange}
                        sx={{mr: theme.spacing(1)}}
                    />
                      <Divider
                      orientation="vertical"
                      flexItem
                      />*/}
                {hasRole('can-use-note') ? (
                  <>
                    <IconButton
                      disabled
                      sx={{ mr: theme.spacing(1), ml: theme.spacing(1) }}
                    >
                      <RateReview />
                    </IconButton>
                  </>
                ) : null}
                {hasRole('can-use-deletion') ? (
                  <>
                    <IconButton disabled>
                      <DoNotDisturbAlt />
                    </IconButton>
                  </>
                ) : null}
              </ActionBar>
              <SortContainer>
                <Typography variant="body2">
                  {t('review_count')}: {paging.totalItems}
                </Typography>
                <FormControl
                  size="small"
                  sx={{ minWidth: '284px' }}
                >
                  <InputLabel id="sort-select-label">{t('sort')}</InputLabel>
                  <Select
                    labelId="sort-select-label"
                    value={orderBy}
                    input={<OutlinedInput label={t('sort')} />}
                    onChange={handleOrderByChange}
                    label={t('sort')}
                  >
                    <MenuItem value="score asc">{t('score_asc')}</MenuItem>
                    <MenuItem value="score desc">{t('score_desc')}</MenuItem>
                    <MenuItem value="date asc">{t('date_asc')}</MenuItem>
                    <MenuItem value="date desc">{t('date_desc')}</MenuItem>
                  </Select>
                </FormControl>
              </SortContainer>
              {loading ? (
                <Box>
                  <LinearProgress sx={{ my: theme.spacing(1) }} />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {!noRecordsFound &&
                    reviewEntries.length === 0 &&
                    getAlert(t('inactive_filter'), t('please_apply_filter'))}
                  {noRecordsFound ? (
                    getAlert(t('no_records'), t('no_records_found'))
                  ) : (
                    <>
                      {reviewEntries.map((reviewEntry) => {
                        const reviewLocation = getReviewLocation(reviewEntry.company);
                        return (
                          <GMBReview
                            key={reviewEntry.review.reviewId}
                            rating={reviewEntry.review.rating}
                            comment={reviewEntry.review.comment}
                            createTime={reviewEntry.review.createTime}
                            name={reviewEntry.review.name}
                            reviewId={reviewEntry.review.reviewId}
                            postgresLocationId={reviewLocation.ID}
                            reviewReply={reviewEntry.review.reviewReply}
                            reviewer={reviewEntry.review.reviewer}
                            starRating={reviewEntry.review.starRating}
                            updateTime={reviewEntry.review.updateTime}
                            checked={selectedReviewIds.includes(reviewEntry.id)}
                            onChange={handleReviewSelectionChange}
                            communityManagementSettings={reviewLocation.communityManagement}
                            note={reviewEntry.note}
                            deletion={reviewEntry.deletion}
                            id={reviewEntry.id}
                            isUpdated={isUpdated}
                            currentUpdatedStatus={reviewEntry.isUpdated}
                            deleted={reviewEntry.withDeleted}
                            reviewEntry={reviewEntry}
                            onReviewCommentChange={handleReviewCommentChange}
                            subHeader={
                              <GoogleLocationInfo
                                locationId={reviewEntry.company}
                                locations={locations}
                              />
                            }
                          />
                        );
                      })}
                    </>
                  )}
                </Box>
              )}
              <TableBulkActionBar
                selectedIds={selectedReviewIds}
                visibleIds={visibleReviewIds}
                onToggleSelectAllVisible={handleToggleSelectAllVisible}
                actions={bulkActions}
                disabled={loading}
                actionSelectLabel={t('action')}
                executeLabel={t('execute')}
                selectedText={t('selected')}
              />
              <Pagination
                count={paging.pagesTotal}
                page={page}
                itemDescription={t('reviews')}
                showItemsPerPage={String(pageSize)}
                onPageChange={handlePageNumberChange}
                onItemsPerPageChange={handlePageSizeChange}
              />
            </LeftContainer>
            <RightContainer>
              <Filter
                disabled={loading}
                filters={
                  <>
                    {hasRole('can-use-ai') && (
                      <CommentStatusFilter
                        disabled={loading}
                        value={commentStatus}
                        onChange={(event: SelectChangeEvent) => {
                          setCommentStatus(event.target.value as CommentStatus);
                        }}
                      />
                    )}
                    <FormControl
                      disabled={loading}
                      size="small"
                      sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                    >
                      <Autocomplete
                        value={filterByLocation}
                        getOptionLabel={(l: any) => l.locationData.title}
                        getOptionKey={(l: any) => l.locationID}
                        options={locations}
                        filterOptions={createFilterOptions({
                          stringify: (option: any) =>
                            `${option.locationData.title} ${option.locationData.address.addressLines} ${option.locationData.address.postalCode} ${option.locationData.address.locality}`,
                        })}
                        onChange={handleLocationFilterChange}
                        renderOption={renderedLocationFilterName}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label={t('location')}
                          />
                        )}
                      />
                    </FormControl>
                    <Typography
                      variant="caption"
                      sx={{
                        textTransform: 'uppercase',
                        fontWeight: '700',
                        mt: theme.spacing(2),
                        color: loading ? theme.palette.text.disabled : theme.palette.text.primary,
                      }}
                    >
                      {t('score')}
                    </Typography>
                    <SliderStarsAsLabels
                      disabled={loading}
                      value={score}
                      onChange={handleScoreFilterChange}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        textTransform: 'uppercase',
                        fontWeight: '700',
                        mt: theme.spacing(2),
                        color: loading ? theme.palette.text.disabled : theme.palette.text.primary,
                      }}
                    >
                      {t('date')}
                    </Typography>
                    <DateRange
                      disabled={loading}
                      fromDate={startDate}
                      onFromDateChange={handleOnFromDateChange}
                      toDate={endDate}
                      onToDateChange={handleOnToDateChange}
                    />
                    <FormGroup sx={{ my: theme.spacing(1) }}>
                      <FormControlLabel
                        disabled={loading}
                        control={
                          <Switch
                            color={loading ? 'default' : 'primary'}
                            checked={withComments}
                            onChange={() => {
                              if (withComments && !withoutComments) {
                                setWithComments(true);
                                setWithoutComments(true);
                              } else {
                                setWithComments((prevState) => !prevState);
                              }
                              prepareFilter();
                            }}
                            inputProps={{ 'aria-label': 'controlled' }}
                          />
                        }
                        label={<Typography variant="body2">{t('reviews_with_comment')}</Typography>}
                      />
                      <FormControlLabel
                        disabled={loading}
                        control={
                          <Switch
                            color={loading ? 'default' : 'primary'}
                            checked={withoutComments}
                            onChange={() => {
                              if (withoutComments && !withComments) {
                                setWithComments(true);
                                setWithoutComments(true);
                              } else {
                                setWithoutComments((prevState) => !prevState);
                              }
                              prepareFilter();
                            }}
                            inputProps={{ 'aria-label': 'controlled' }}
                          />
                        }
                        label={
                          <Typography variant="body2">{t('reviews_without_comment')}</Typography>
                        }
                      />
                    </FormGroup>
                    {hasRole('can-use-deletion') ? (
                      <>
                        <FormGroup>
                          <FormControlLabel
                            disabled={loading}
                            control={
                              <Switch
                                checked={withDeletion}
                                onChange={handleDeletionChange}
                                inputProps={{ 'aria-label': 'controlled' }}
                              />
                            }
                            label={
                              <Typography variant="body2">{t('reviews_to_delete')}</Typography>
                            }
                          />
                        </FormGroup>
                      </>
                    ) : null}
                    {hasRole('can-use-note') ? (
                      <>
                        <FormGroup>
                          <FormControlLabel
                            disabled={loading}
                            control={
                              <Switch
                                checked={withNote}
                                onChange={handleNoteChange}
                                inputProps={{ 'aria-label': 'controlled' }}
                              />
                            }
                            label={
                              <Typography variant="body2">{t('reviews_with_notes')}</Typography>
                            }
                          />
                        </FormGroup>
                      </>
                    ) : null}
                    {/*{ hasRole("can-use-deleted-reviews") ? (
                      <>
                      <FormGroup>
                      <FormControlLabel
                      disabled={loading}
                    control={
                      <Switch
                        checked={withDeleted}
                        onChange={() => {
                          setWithDeleted((prevState) => !prevState);
                          prepareFilter();
                        }}
                        inputProps={{ "aria-label": "controlled" }}
                      />
                    }
                    label={
                      <Typography variant="body2">{t('deleted_reveiws')}</Typography>
                    }
                  />
                  </FormGroup>
                  </>
                  ) : null}*/}
                    <FormGroup>
                      <FormControlLabel
                        disabled={loading}
                        control={
                          <Switch
                            color={loading ? 'default' : 'primary'}
                            checked={isUpdated}
                            onChange={() => {
                              setIsUpdated((prevState) => !prevState);
                              prepareFilter();
                            }}
                            inputProps={{ 'aria-label': 'controlled' }}
                          />
                        }
                        label={<Typography variant="body2">{t('updated_ratings')}</Typography>}
                      />
                    </FormGroup>
                    <Divider sx={{ mt: theme.spacing(1) }} />
                    <Button
                      disabled={loading}
                      variant="contained"
                      onClick={() => {
                        setOffset(0);
                        setPage(1);
                        setApplyFilter(true);
                      }}
                      sx={{ mt: theme.spacing(1) }}
                    >
                      {t('apply')}
                    </Button>
                  </>
                }
                activeFilters={
                  <>
                    {commentStatus && (
                      <Chip
                        label={`Status: ${t(commentStatus)}`}
                        onDelete={() => {
                          setCommentStatus('');
                        }}
                      />
                    )}
                    {location.title && applyFilter && (
                      <Chip
                        label={location.title}
                        onDelete={() => {
                          setReviewEntries([]);
                          setLocation(defaultLocation);
                          setPaging((prevState) => ({
                            ...prevState,
                            pagesTotal: 0,
                            totalItems: 0,
                          }));
                        }}
                      />
                    )}
                    {(score[0] !== 1 || score[1] !== 5) && applyFilter && (
                      <Chip
                        label={`${t('stars')} ${score[0]} - ${score[1]}`}
                        onDelete={() => {
                          setScore([1, 5]);
                          prepareFilter();
                        }}
                      />
                    )}
                    {startDate && isValid(startDate) && !endDate && applyFilter && (
                      <Chip
                        label={`${t('date_from')}: ${format(startDate, 'dd.MM.yyyy')}`}
                        onDelete={() => {
                          setStartDate(null);
                          prepareFilter();
                        }}
                      />
                    )}
                    {!startDate && endDate && isValid(endDate) && applyFilter && (
                      <Chip
                        label={`${t('date_to')}: ${format(endDate, 'dd.MM.yyyy')}`}
                        onDelete={() => {
                          setEndDate(null);
                          prepareFilter();
                        }}
                      />
                    )}
                    {startDate &&
                      isValid(startDate) &&
                      endDate &&
                      isValid(endDate) &&
                      applyFilter && (
                        <Chip
                          label={`${format(startDate, 'dd.MM.yyyy')} - ${format(
                            endDate,
                            'dd.MM.yyyy'
                          )}`}
                          onDelete={() => {
                            setStartDate(null);
                            setEndDate(null);
                            prepareFilter();
                          }}
                        />
                      )}
                    {withComments && !withoutComments && applyFilter && (
                      <Chip
                        label={t('reviews_with_comment')}
                        onDelete={() => {
                          setWithoutComments(true);
                          prepareFilter();
                        }}
                      />
                    )}
                    {withDeletion && applyFilter && (
                      <Chip
                        label={t('reviews_to_delete')}
                        onDelete={() => {
                          setWithDeletion(true);
                          prepareFilter();
                        }}
                      />
                    )}
                    {withNote && applyFilter && (
                      <Chip
                        label={t('reviews_with_notes')}
                        onDelete={() => {
                          setWithNote(true);
                          prepareFilter();
                        }}
                      />
                    )}
                    {withoutComments && !withComments && applyFilter && (
                      <Chip
                        label={t('reviews_without_comment')}
                        onDelete={() => {
                          setWithComments(true);
                          prepareFilter();
                        }}
                      />
                    )}
                    {isUpdated && applyFilter && (
                      <Chip
                        label={'Aktualisiert Bewertungen'}
                        onDelete={() => {
                          setIsUpdated(false);
                          prepareFilter();
                        }}
                      />
                    )}
                    {withDeleted && applyFilter && (
                      <Chip
                        label={t('deleted_reveiws')}
                        onDelete={() => {
                          setWithDeleted(false);
                          prepareFilter();
                        }}
                      />
                    )}
                    {withComments &&
                      withoutComments &&
                      !withDeletion &&
                      !withNote &&
                      applyFilter && <Chip label={t('all_reviews')} />}
                  </>
                }
                resetActiveFilters={() => {
                  setReviewEntries([]);
                  setLocation(defaultLocation);
                  setScore([1, 5]);
                  setStartDate(null);
                  setEndDate(null);
                  setWithComments(true);
                  setWithoutComments(true);
                  setWithNote(false);
                  setWithDeletion(false);
                  setWithDeleted(false);
                  setOffset(0);
                  setPage(1);
                  setPageSize(10);
                  setIsUpdated(false);
                  setPaging((prevState) => ({
                    ...prevState,
                    pagesTotal: 0,
                    totalItems: 0,
                  }));
                }}
              />
            </RightContainer>
          </ContentContainer>
        )}
      </>
    </Section>
  );

  function getAlert(title: string, message: string) {
    return (
      <Alert
        severity="warning"
        sx={{
          minWidth: '450px',
          maxWidth: '650px',
          alignSelf: 'center',
          my: theme.spacing(1),
        }}
      >
        <AlertTitle sx={{ fontWeight: '700' }}>{title}</AlertTitle>
        {message}
      </Alert>
    );
  }
};

export default Reviews;
